import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useFragment } from "react-relay";
import { useRouteMatch } from "react-router-dom";

import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { noop } from "lodash";
import { graphql } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import MeetingPhaseContext, {
	type RecordingMediaType,
	type RecordingMode,
	someConferenceCallSupportsRecording,
} from "~/stream/MeetingPhaseContext";
import { getCallName } from "~/stream/NoteHeader/JoinConferenceButton";
import { Tooltip } from "~/ui";
import type { SingleValue } from "~/ui/selectors/Selector";

import { DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG, WEB_BOTLESS_RECORDING_FEATURE_FLAG } from "../featureFlag";
import AutoRecordToggle from "./AutoRecordToggle";
import OptionsMenu from "./OptionsMenu";
import StartRecordingButton from "./StartRecordingButton";

import type {
	PreMeetingControls_note$data as Note,
	PreMeetingControls_note$key,
} from "~/__generated__/PreMeetingControls_note.graphql";
import type { PreMeetingControls_setSettingMutation as MutationType } from "~/__generated__/PreMeetingControls_setSettingMutation.graphql";
import type { PreMeetingControls_viewer$key } from "~/__generated__/PreMeetingControls_viewer.graphql";

type ConferenceCall = NonNullable<Note["event"]>["conferenceCalls"][number];
export type ConferenceCallOption = { label: string; value: ConferenceCall["provider"]; conferenceCall: ConferenceCall };

interface RecordingOptionsContextType {
	recordingMediaType: RecordingMediaType;
	recordingMode: RecordingMode;
	setRecordingMediaType: (format: RecordingMediaType) => void;
	setRecordingMode: (mode: RecordingMode) => void;
	conferenceCallOptions: ConferenceCallOption[];
	selectedConferenceCall: ConferenceCallOption | null;
	onChangeConferenceCall: (option: SingleValue<ConferenceCallOption>) => void;
}

export const RecordingOptionsContext = React.createContext<RecordingOptionsContextType>({
	recordingMediaType: "VIDEO",
	recordingMode: "BOT",
	setRecordingMediaType: noop,
	setRecordingMode: noop,
	conferenceCallOptions: [],
	selectedConferenceCall: null,
	onChangeConferenceCall: noop,
});

RecordingOptionsContext.displayName = "RecordingOptionsContext";

const viewerFragment = graphql`
	fragment PreMeetingControls_viewer on Viewer {
		...AutoRecordToggle_viewer
		...OptionsMenu_viewer
	}
`;

const noteFragment = graphql`
	fragment PreMeetingControls_note on Note {
		event {
			conferenceCalls {
				provider
				url
			}
		}

		stream {
			id
			recordingMediaType
			recordingMode
		}

		...AutoSendToEveryone_autoShareIsSetToEveryone_note
		...AutoRecordToggle_note
		...OptionsMenu_note
	}
`;

const setSettingMutation = graphql`
	mutation PreMeetingControls_setSettingMutation($streamId: ID!, $setting: String!, $value: String) {
		noteStreamSetting(streamId: $streamId, setting: $setting, value: $value) {
			stream {
				id
				recordingMediaType
				recordingMode
			}
		}
	}
`;

function updateRecordingOption(
	streamId: string,
	opts:
		| {
				option: "recording_media_type";
				value: RecordingMediaType;
		  }
		| {
				option: "recording_mode";
				value: RecordingMode;
		  },
) {
	return commitMutationPromise<MutationType>(environment, {
		mutation: setSettingMutation,
		variables: {
			streamId,
			setting: opts.option,
			value: opts.value,
		},
		optimisticUpdater: store => {
			const stream = store.get(streamId);
			if (!stream) return;

			if (opts.option === "recording_media_type") {
				stream.setValue(opts.value, "recordingMediaType");
			} else if (opts.option === "recording_mode") {
				stream.setValue(opts.value, "recordingMode");
			}
		},
	});
}

export interface PreMeetingControlsProps {
	viewer: PreMeetingControls_viewer$key;
	note: PreMeetingControls_note$key;
}

export default function PreMeetingControls({ viewer: viewerKey, note: noteKey }: PreMeetingControlsProps) {
	const inCompanionMode = useRouteMatch({ path: "/companion-mode/" }) !== null;
	const { phase, mustUpgradeBeforeRecording: mustUpgrade } = useContext(MeetingPhaseContext);

	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const conferenceCalls: readonly ConferenceCall[] = useMemo(
		() => note.event?.conferenceCalls ?? [],
		[note.event?.conferenceCalls],
	);
	const meetingHasBotRecordableConferenceCall = someConferenceCallSupportsRecording(conferenceCalls);

	const [menuType, setMenuType] = useState<"autoRecord" | "startRecording">("startRecording");

	// -------------------------------------------------------------------------------------------------------------------
	// Options menu layout stuff
	// -------------------------------------------------------------------------------------------------------------------

	const [targetPosition, setTargetPosition] = useState<number | null>(null);
	const openTimeoutRef = useRef<number | null>(null);
	const closeTimeoutRef = useRef<number | null>(null);

	const onMouseEnterTarget = useCallback(
		(menuType: "autoRecord" | "startRecording") => (event: React.MouseEvent<HTMLElement>) => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}

			const targetRect = event.currentTarget.getBoundingClientRect();

			function openMenu() {
				setTargetPosition(targetRect.x + targetRect.width / 2);
				setMenuType(menuType);
			}

			if (!targetPosition) {
				openTimeoutRef.current = window.setTimeout(openMenu, 250);
			} else {
				openMenu();
			}
		},
		[targetPosition],
	);

	const onMouseEnterMenu = useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
	}, []);

	const onMouseLeaveTargetOrMenu = useCallback(() => {
		if (openTimeoutRef.current) {
			clearTimeout(openTimeoutRef.current);
		}

		closeTimeoutRef.current = window.setTimeout(() => {
			setTargetPosition(null);
		}, 100);
	}, []);

	// -------------------------------------------------------------------------------------------------------------------
	// Auto-record and Record now states
	// -------------------------------------------------------------------------------------------------------------------

	const audioOnlyRecordingEnabled = useFeatureIsOn("audio-only-recording");
	const desktopRecordingEnabled = useFeatureIsOn(DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG);
	const isDesktopRecordingAvailable = desktopRecordingEnabled && window.desktopRecorder;
	const microphoneRecordingEnabled = useFeatureIsOn(WEB_BOTLESS_RECORDING_FEATURE_FLAG);

	const mustBeBotless = !meetingHasBotRecordableConferenceCall;

	const recordingMode = useMemo(() => {
		if (!microphoneRecordingEnabled && !isDesktopRecordingAvailable) {
			return "BOT";
		} else if (mustBeBotless) {
			return "BOTLESS";
		} else {
			return note.stream.recordingMode;
		}
	}, [isDesktopRecordingAvailable, microphoneRecordingEnabled, mustBeBotless, note.stream.recordingMode]);
	const setRecordingMode = useCallback(
		(mode: RecordingMode) => {
			// Maintains the initial state above
			if ((!isDesktopRecordingAvailable && !microphoneRecordingEnabled) || mustBeBotless) return;

			updateRecordingOption(note.stream.id, { option: "recording_mode", value: mode });
		},
		[isDesktopRecordingAvailable, microphoneRecordingEnabled, mustBeBotless, note.stream.id],
	);

	const recordingMediaType = useMemo(
		() => (!audioOnlyRecordingEnabled ? "VIDEO" : note.stream.recordingMediaType),
		[audioOnlyRecordingEnabled, note.stream.recordingMediaType],
	);
	const setRecordingMediaType = useCallback(
		(format: RecordingMediaType) => {
			// Maintains the initial state above
			if (!audioOnlyRecordingEnabled) return;

			updateRecordingOption(note.stream.id, { option: "recording_media_type", value: format });
		},
		[audioOnlyRecordingEnabled, note.stream.id],
	);

	const conferenceCallOptions = useMemo<ConferenceCallOption[]>(
		() =>
			conferenceCalls.map(call => ({ label: getCallName(call.provider), value: call.provider, conferenceCall: call })),
		[conferenceCalls],
	);
	// TODO: I think we'll want to ensure that the preferred meeting is used if there are multiple links for events in a series.
	// The links change on every meeting, so it won't be as straightforward as choosing the same conference.
	// I think it would make sense to store the chosen provider (i.e. if they chose a Zoom call despite there being a Meet
	// link) as a stream setting and make sure to choose that going forward.
	// This setting would then need to be considered when choosing which call to join for scheduled bots. -Wal
	const [selectedConferenceCall, setSelectedConferenceCall] = useState<ConferenceCallOption | null>(
		conferenceCalls.length > 0 ? conferenceCallOptions[0] : null,
	);
	const onChangeConferenceCall = useCallback(
		(option: SingleValue<ConferenceCallOption>) => option && setSelectedConferenceCall(option),
		[setSelectedConferenceCall],
	);

	// -------------------------------------------------------------------------------------------------------------------

	// These variables contain some not-straightforward behaviour introduced because of overlapping feature flags.
	const { isStartRecordingVisible, isStartRecordingDisabled } = useMemo(
		() => ({
			// The button should be visible when…
			isStartRecordingVisible:
				// Desktop recording is enabled, since we can record via computer audio, or…
				isDesktopRecordingAvailable ||
				// Microphone recording is enabled, since we can record with mic audio or…
				microphoneRecordingEnabled ||
				// There's a conference call on this meeting that supports bot recording
				meetingHasBotRecordableConferenceCall,
			// The button should be disabled…
			isStartRecordingDisabled:
				// If we can only show the botless option and…
				mustBeBotless &&
				// Microphone recording is disabled and…
				!microphoneRecordingEnabled &&
				// Desktop recording is disabled, or it is enabled and we're not on the desktop app.
				!isDesktopRecordingAvailable,
		}),
		[isDesktopRecordingAvailable, microphoneRecordingEnabled, meetingHasBotRecordableConferenceCall, mustBeBotless],
	);

	return (
		<RecordingOptionsContext.Provider
			value={useMemo(
				() => ({
					recordingMode,
					recordingMediaType,
					setRecordingMode,
					setRecordingMediaType,
					conferenceCallOptions,
					selectedConferenceCall,
					onChangeConferenceCall,
				}),
				[
					recordingMode,
					recordingMediaType,
					setRecordingMode,
					setRecordingMediaType,
					conferenceCallOptions,
					selectedConferenceCall,
					onChangeConferenceCall,
				],
			)}
		>
			{!mustUpgrade && (
				<OptionsMenu
					viewer={viewer}
					note={note}
					menuType={menuType}
					targetPosition={targetPosition}
					onMouseEnterMenu={onMouseEnterMenu}
					onMouseLeaveMenu={onMouseLeaveTargetOrMenu}
				/>
			)}

			{!mustUpgrade && phase === "before" && meetingHasBotRecordableConferenceCall && !inCompanionMode && (
				<AutoRecordToggle
					viewer={viewer}
					note={note}
					onMouseEnterTarget={onMouseEnterTarget("autoRecord")}
					onMouseLeaveTarget={onMouseLeaveTargetOrMenu}
				/>
			)}

			{isStartRecordingVisible && (
				<Tooltip
					content={
						<div style={{ textAlign: "center" }}>
							The desktop app enables Fellow to record meetings without a conference call.
						</div>
					}
					maxWidth={250}
					disabled={!isStartRecordingDisabled}
				>
					<span>
						<StartRecordingButton
							onMouseEnterTarget={onMouseEnterTarget("startRecording")}
							onMouseLeaveTarget={onMouseLeaveTargetOrMenu}
							disabled={isStartRecordingDisabled}
						/>
					</span>
				</Tooltip>
			)}
		</RecordingOptionsContext.Provider>
	);
}
